import { useCallback } from "react";
import { Link2, Copy } from "lucide-react";
import { cn } from "@/utils/cn";
import { useToast } from "@/hooks/useToast";
import { APP_BASE_URL, getInitials } from "@/utils/util";
import { Text } from "@/components/Typography";
import { NormalButton } from "@/components/NormalButton";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/Avatar";
import type { ExpertOKT, OKT } from "@/services/api";
import type { TaskStatus } from "@/types/tasks";
import { EXPERT_SIGNUP_ROLE_PARAM } from "@/utils/roleUtils";

// ── Task drawer adapter ───────────────────────────────────────────────────────

/**
 * Adapt an Expert Program task to the `OKT` shape `TaskEditForm` expects, so
 * the Program creation and detail pages can reuse the same task drawer the
 * Goals page uses instead of hand-rolling an inline row editor.
 *
 * The two types differ only in fields the form never reads back (`description`
 * is nullable on ExpertOKT; `isSuggested`/`evaluationComment` don't exist), so
 * they're filled with inert defaults.
 */
export function toTaskEditFormTask(okt: ExpertOKT): OKT {
  return {
    id: okt.id,
    title: okt.title,
    description: okt.description ?? "",
    status: okt.status as TaskStatus,
    startDate: okt.startDate,
    dueDate: okt.dueDate,
    isSuggested: false,
    isFinalized: true,
    evaluationComment: null,
    notesCount: okt.notesCount,
    clonedFromId: okt.clonedFromId,
  };
}

// ── Invite link builders ──────────────────────────────────────────────────────

// Enrollment links deliberately carry NO organisation code.
//
// These used to stamp `code=YEP` on every link, which put every enrolling
// achiever into one pseudo-company. That is wrong now that enrolment is paid:
// company membership is what decides whether someone is sponsored, so tagging
// everyone with YEP would either comp them all or misattribute them. Without a
// code the user simply has no company, which is the correct default -- they
// pay for the program they're joining.
export function buildAchieverInviteLink(slug: string | null, externalId: string): string {
  return `${APP_BASE_URL}/join/${slug ?? externalId}?role=achiever`;
}

export function buildAdvisorInviteLink(slug: string | null, externalId: string): string {
  return `${APP_BASE_URL}/signup?role=${EXPERT_SIGNUP_ROLE_PARAM}&join=${slug ?? externalId}`;
}

/** @deprecated Use buildAchieverInviteLink or buildAdvisorInviteLink */
export function buildInviteLink(slug: string | null, externalId: string): string {
  return buildAchieverInviteLink(slug, externalId);
}

// ── Avatar stack ──────────────────────────────────────────────────────────────
//
// Lives here rather than in AdvisorProgramPage because two card types now use
// it: the Expert card (co-experts) and the Peer Advisory card (facilitators).
// Importing it from the page module would have made
// components/PeerAdvisoryProgramCard <-> AdvisorProgramPage a cycle.

const CO_EXPERT_AVATAR_GRADIENTS = [
  "var(--prog-avatar-grad-1)",
  "var(--prog-avatar-grad-2)",
  "var(--prog-avatar-grad-3)",
  "var(--prog-avatar-grad-4)",
];

export interface ProgramMemberSummary {
  user_id: string;
  full_name?: string | null;
  profile_picture_url?: string | null;
}

export const AvatarStack = ({ members }: { members: ProgramMemberSummary[] }) => {
  if (members.length === 0) return null;
  const visible = members.slice(0, 2);
  const extra = members.length - 2;
  return (
    <div className="flex items-center -space-x-2">
      {visible.map((m, i) => {
        const name = m.full_name ?? "";
        return (
          <div
            key={m.user_id}
            className="relative group/avatar flex-shrink-0"
            style={{ zIndex: 2 - i }}
          >
            <Avatar
              className="!w-7 !h-7 flex-shrink-0 overflow-hidden rounded-full"
              style={{ border: "2px solid var(--color-prog-page-dark-bg, #0C1220)" }}
            >
              {m.profile_picture_url && (
                <AvatarImage
                  src={m.profile_picture_url}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              )}
              <AvatarFallback
                className="text-[10px] font-bold text-white flex h-full w-full items-center justify-center rounded-full"
                style={{
                  background: CO_EXPERT_AVATAR_GRADIENTS[i % CO_EXPERT_AVATAR_GRADIENTS.length],
                }}
              >
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            {name && (
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap rounded bg-neutral-800 px-2 py-0.5 text-[10px] text-white opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none z-10">
                {name}
              </span>
            )}
          </div>
        );
      })}
      {extra > 0 && (
        <div
          className="rounded-full flex items-center justify-center border-2 text-[9px] font-semibold flex-shrink-0 w-7 h-7"
          style={{
            background: "var(--prog-row-bg)",
            color: "var(--prog-text-muted)",
            borderColor: "var(--color-prog-page-dark-bg, #0C1220)",
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
};

// ── Meta chip ─────────────────────────────────────────────────────────────────

export interface MetaChipProps {
  icon: React.ElementType;
  label: string;
  highlight?: boolean;
}

export const MetaChip = ({ icon: Icon, label, highlight }: MetaChipProps) => (
  <div className="flex items-center gap-[6px]">
    <Icon
      size={12}
      className="flex-shrink-0"
      style={{ color: highlight ? "var(--prog-status-enroll-text)" : "var(--prog-text-muted)" }}
    />
    <span
      className="font-inter font-normal leading-4"
      style={{
        fontSize: "12px",
        color: highlight ? "var(--prog-status-enroll-text)" : "var(--prog-text-muted)",
        fontWeight: highlight ? 500 : undefined,
      }}
    >
      {label}
    </span>
  </div>
);

// ── Status badge ──────────────────────────────────────────────────────────────

type ProgramStatus =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "REJECTED"
  | "ACTIVE"
  | "COMPLETED"
  | "ARCHIVED"
  | "ENROLLING";

type StatusConfig = {
  dotVar: string;
  bgVar: string;
  borderVar: string;
  textVar: string;
  label: string;
};

const STATUS_MAP: Record<ProgramStatus, StatusConfig> = {
  ENROLLING: {
    dotVar: "var(--prog-status-enroll-dot)",
    bgVar: "var(--prog-status-enroll-bg)",
    borderVar: "var(--prog-status-enroll-border)",
    textVar: "var(--prog-status-enroll-text)",
    label: "Enrolling",
  },
  ACTIVE: {
    dotVar: "var(--prog-status-live-dot)",
    bgVar: "var(--prog-status-live-bg)",
    borderVar: "var(--prog-status-live-border)",
    textVar: "var(--prog-status-live-text)",
    label: "Live",
  },
  DRAFT: {
    dotVar: "var(--color-prog-draft-dot)",
    bgVar: "var(--stars-tutorial-close-btn-bg)",
    borderVar: "var(--stars-tutorial-close-btn-bg)",
    textVar: "var(--prog-text-muted)",
    label: "Draft",
  },
  // Submitted and awaiting Forward-team review. Shares the amber "enrolling"
  // treatment (attention, not yet final) rather than the muted draft one, so
  // an expert can tell at a glance which drafts they've already sent in.
  PENDING_REVIEW: {
    dotVar: "var(--prog-status-enroll-dot)",
    bgVar: "var(--prog-status-enroll-bg)",
    borderVar: "var(--prog-status-enroll-border)",
    textVar: "var(--prog-status-enroll-text)",
    label: "In Review",
  },
  REJECTED: {
    dotVar: "var(--prog-status-rejected-dot, #f87171)",
    bgVar: "var(--prog-status-rejected-bg, rgba(248,113,113,0.12))",
    borderVar: "var(--prog-status-rejected-border, rgba(248,113,113,0.35))",
    textVar: "var(--prog-status-rejected-text, #f87171)",
    label: "Changes Needed",
  },
  COMPLETED: {
    dotVar: "var(--prog-text-muted)",
    bgVar: "var(--prog-row-bg)",
    borderVar: "var(--prog-card-border)",
    textVar: "var(--prog-text-muted)",
    label: "Completed",
  },
  ARCHIVED: {
    dotVar: "var(--prog-text-muted)",
    bgVar: "var(--prog-row-bg)",
    borderVar: "var(--prog-card-border)",
    textVar: "var(--prog-text-muted)",
    label: "Archived",
  },
};

export const StatusBadge = ({ status, className }: { status: string; className?: string }) => {
  const cfg = STATUS_MAP[status as ProgramStatus] ?? STATUS_MAP.DRAFT;
  return (
    <Text
      as="span"
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full border text-[11px] font-semibold whitespace-nowrap",
        className,
      )}
      style={{
        background: cfg.bgVar,
        borderColor: cfg.borderVar,
        color: cfg.textVar,
      }}
    >
      <span
        aria-hidden="true"
        className="w-1.5 h-1.5 rounded-full flex-shrink-0 inline-block"
        style={{ background: cfg.dotVar }}
      />
      {cfg.label}
    </Text>
  );
};

// ── Invite link bar ───────────────────────────────────────────────────────────

export const InviteLinkBar = ({ link }: { link: string }) => {
  const { showSuccessToast } = useToast();
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(link).then(() => showSuccessToast("", "Link Copied"));
  }, [link, showSuccessToast]);

  return (
    <div
      className="flex items-center gap-2 rounded-[14px] px-3 py-2.5"
      style={{
        background: "var(--prog-link-bar-bg-color)",
        border: "1px solid var(--prog-link-bar-border-color)",
      }}
    >
      <Link2 size={13} className="flex-shrink-0" style={{ color: "var(--prog-text-muted)" }} />
      <Text
        as="span"
        className="flex-1 min-w-0 truncate font-mono text-[12px]"
        style={{ color: "var(--prog-text-muted)" }}
      >
        {link}
      </Text>
      <NormalButton
        type="button"
        size="sm"
        bgColor="bg-transparent"
        borderColor="border-transparent"
        hoverBgColor="hover:opacity-80"
        onClick={handleCopy}
        className="gap-1 rounded-[10px] px-2 py-0.5 text-[12px] font-semibold transition-opacity flex-shrink-0"
        style={{ color: "var(--prog-copy-link-text)" }}
        leftIcon={<Copy size={12} />}
      >
        Copy
      </NormalButton>
    </div>
  );
};
